/**
 * Secure email validation utility for frontend
 *
 * This implementation uses a non-backtracking regex approach to prevent ReDoS attacks.
 * The regex is designed to be linear in time complexity and safe from catastrophic backtracking.
 */
/**
 * Validates an email address using a secure, non-backtracking regex pattern
 * that prevents ReDoS (Regular Expression Denial of Service) attacks.
 *
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 */
export function isValidEmail(email) {
    if (!email || typeof email !== "string") {
        return false;
    }
    // Trim whitespace
    const trimmedEmail = email.trim();
    // Basic length checks
    if (trimmedEmail.length < 5 || trimmedEmail.length > 254) {
        return false;
    }
    // Use a secure, non-backtracking regex pattern
    // This pattern avoids nested quantifiers and uses atomic grouping concepts
    // Pattern breakdown:
    // ^ - start of string
    // [a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+ - local part (one or more valid chars)
    // @ - literal @ symbol
    // [a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])? - domain name (non-backtracking)
    // \. - literal dot
    // [a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])? - TLD (non-backtracking)
    // $ - end of string
    const secureEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return secureEmailRegex.test(trimmedEmail);
}
/**
 * Validates an email address with additional security checks
 * including protection against extremely long local parts and domains
 *
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 */
export function isValidEmailSecure(email) {
    if (!isValidEmail(email)) {
        return false;
    }
    const trimmedEmail = email.trim();
    const [localPart, domain] = trimmedEmail.split("@");
    // Additional security checks
    if (!localPart || !domain) {
        return false;
    }
    // RFC 5321 limits local part to 64 characters
    if (localPart.length > 64) {
        return false;
    }
    // RFC 5321 limits domain to 253 characters
    if (domain.length > 253) {
        return false;
    }
    // Check for consecutive dots in local part
    if (localPart.includes("..")) {
        return false;
    }
    // Check for consecutive dots in domain
    if (domain.includes("..")) {
        return false;
    }
    // Domain must not start or end with a dot
    if (domain.startsWith(".") || domain.endsWith(".")) {
        return false;
    }
    return true;
}
/**
 * Sanitizes an email address by trimming whitespace and converting to lowercase
 *
 * @param email - The email address to sanitize
 * @returns The sanitized email address or null if invalid
 */
export function sanitizeEmail(email) {
    if (!email || typeof email !== "string") {
        return null;
    }
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmailSecure(trimmed)) {
        return null;
    }
    return trimmed;
}
